import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input"; //
import "react-phone-number-input/style.css";
import AlertModal from "./common/AlertModal";
import FooterPanel from "./common/FooterPanel";
import MenuBar from "./common/MenuPanel";
import UtilMethods from "../util/UtilMethods";
import footerStringsEN from "../locales/en/footer.json";
import footerStringsES from "../locales/es/footer.json";
import { useTranslation } from "react-i18next";

const ContactUsPage = () => {
  const navigate = useNavigate();

  const { t ,i18n} = useTranslation(["contact", "common", "labels"]);
  const footerStrings = i18n.language.startsWith("es")
    ? footerStringsES
    : footerStringsEN;
  const { contactInfo, contactMarianInfo } = footerStrings;

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "firstName") setFirstName(value);
    if (name === "email") setEmail(value);
    if (name === "message") setMessage(value);
  };

  const handlePhoneChange = (value) => setPhone(value);
  const validateForm = () => {
    const newErrors = {};
    if (!firstName.trim()) newErrors.firstName = t("errors.nameRequired");
    if (!email.trim()) newErrors.email = t("errors.emailRequired");
    if (!phone) newErrors.phone = t("errors.phoneRequired");
    if (!message.trim()) newErrors.message = t("errors.messageRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirmModal(true);
  };

  const handleConfirm = async (confirmed) => {
    setShowConfirmModal(false);
    if (!confirmed) {
      setAlertMessage(t("formCanceled"));
      setAlertType("warning");
      return;
    }
    setIsSubmitting(true);
    try {
      await UtilMethods.sendContactEmail(firstName, email, phone, message);
      setAlertMessage(t("successEmail"));
      setAlertType("success");
    } catch (error) {
      console.error("Error submitting email:", error);
      setAlertMessage(t("emailError"));
      setAlertType("danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/");

  return (
    <React.Fragment>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">{t("title")}</h1>
          </div>
        </div>
      </div>

      <div className="container my-4 centered">
        {/* Marian and Grace Info */}
        <div className="row mb-2 g-3 justify-content-center">
          <div className="col-md-6">
            <h2 className="text-left mb-4">{t("marian")}</h2>
            {(Array.isArray(contactMarianInfo) ? contactMarianInfo : []).map(
              (info, index) => (
                <div key={index} className="mb-2">
                  <a
                    href={info.href || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-flex align-items-start"
                    style={{ color: "#5b2816d6" }}
                  >
                    <i className={`${info.icon} me-2`} />
                    {UtilMethods.formatTextWithLineBreaks(info.text)}
                  </a>
                </div>
              )
            )}
          </div>
          <div className="col-md-6">
            <h2 className="text-left mb-4">{t("grace")}</h2>
            {(Array.isArray(contactInfo) ? contactInfo : []).map(
              (info, index) => (
                <div key={index} className="mb-2">
                  <a
                    href={info.href || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none d-flex align-items-start"
                    style={{ color: "#5b2816d6" }}
                  >
                    <i className={`${info.icon} me-2`} />
                    {UtilMethods.formatTextWithLineBreaks(info.text)}
                  </a>
                </div>
              )
            )}
          </div>
        </div>

        {/* Contact Text */}
        <div className="text-center mb-4">
          <h2>{t("title")}</h2>
          <h4>{t("scripture")}</h4>
          <p className="mb-1">
            {t("reachOut")}{" "}
            <a href="mailto:info@graceanointing.org">info@graceanointing.org</a>{" "}
            {t("leaveDetails")}
          </p>
          <p>
            <strong>{t("forPrayerRequest")}</strong>:{" "}
            <a href={`${process.env.PUBLIC_URL}/prayer`}>
              {t("clickHere")} &gt; {t("prayer")}
            </a>
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="form">
          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-user" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={t("placeholders.name")} // Displays: "Your Name"
                name="firstName"
                value={firstName}
                required
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            {errors.firstName && (
              <div className="text-danger small mt-1">{errors.firstName}</div>
            )}
          </div>

          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-envelope" />
              </span>
              <input
                type="email"
                className="form-control"
                placeholder={t("placeholders.email")}
                name="email"
                value={email}
                required
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
            {errors.email && (
              <div className="text-danger small mt-1">{errors.email}</div>
            )}
          </div>

          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-phone" />
              </span>
              <PhoneInput
                flagUrl="https://flagcdn.com/w320/{xx}.png"
                className="form-select"
                placeholder={t("placeholders.phone")}
                defaultCountry="US"
                value={phone}
                required
                onChange={handlePhoneChange}
                disabled={isSubmitting}
              />
            </div>
            {errors.phone && (
              <div className="text-danger small mt-1">{errors.phone}</div>
            )}
          </div>

          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-comment" />
              </span>
              <textarea
                className="form-control"
                placeholder={t("placeholders.message")}
                name="message"
                value={message}
                onChange={handleChange}
                required
                rows="4"
                disabled={isSubmitting}
              />
            </div>
            {errors.message && (
              <div className="text-danger small mt-1">{errors.message}</div>
            )}
          </div>

          <div className="d-flex justify-content-between gap-2 mb-4">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-light flex-grow-1"
            >
              {t("buttons.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-grow-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                t("buttons.submit")
              )}
            </button>
          </div>
        </form>

        {alertMessage && (
          <div
            className={`alert alert-${alertType}`}
            role="alert"
            aria-live="assertive"
          >
            {alertMessage}
          </div>
        )}
      </div>

      <AlertModal
        show={showConfirmModal}
        type="confirmation"
        title={t("confirmTitle")}
        message={t("confirmMessage")}
        details={{}}
        onCancel={() => handleConfirm(false)}
        onConfirm={() => handleConfirm(true)}
      />

      <FooterPanel />
    </React.Fragment>
  );
};

export default ContactUsPage;
